import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Loader2 } from 'lucide-react'
import { createVM, startVM } from '../lib/api'
import Button from './Button'

// CreateVMModal drives the self-service "get a VM" flow. It POSTs /vms to create
// the inventory record, then (opt-in) tries POST /vms/:id/start. On a host without
// a KVM-capable engine the start will fail — we surface that honestly rather than
// pretend the VM is running.
export default function CreateVMModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [id, setId] = useState('')
  const [memory, setMemory] = useState('256M')
  const [kernel, setKernel] = useState('lts')
  const [autoStart, setAutoStart] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      // rootfs is required by the API; "default" selects the host golden image.
      await createVM({ id: id.trim(), memory, kernel_profile: kernel, rootfs: 'default' })
      if (autoStart) {
        // Best-effort boot. A failure here does NOT undo the create — the VM
        // exists as inventory and can be started later once a KVM host is wired.
        await startVM(id.trim())
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vms'] })
      queryClient.invalidateQueries({ queryKey: ['vm-usage'] })
      onClose()
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Create failed'
      // The create likely succeeded but the boot failed (no KVM engine on this host).
      // Keep the modal open, refresh the list, and tell the truth.
      queryClient.invalidateQueries({ queryKey: ['vms'] })
      if (autoStart && /start|boot|engine|systemctl|neutron/i.test(msg)) {
        setNotice(
          `VM "${id.trim()}" was created but could not be booted on this host yet ` +
            `(no KVM engine available). It will appear in your list as "stopped".`
        )
      } else {
        setError(msg)
      }
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    if (!/^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,63}$/.test(id.trim())) {
      setError('ID must be alphanumeric (with _.-), start with a letter/number, max 64 chars')
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Create VM</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {notice && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              {notice}
              <button type="button" onClick={onClose} className="mt-2 block font-medium underline">
                View in list
              </button>
            </div>
          )}

          <label className="block">
            <span className="text-xs font-medium text-gray-700">VM name / ID</span>
            <input
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="my-first-vm"
              autoFocus
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-voltainer-500 focus:border-transparent"
            />
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Memory</span>
              <select
                value={memory}
                onChange={(e) => setMemory(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-voltainer-500"
              >
                <option value="256M">256 MB</option>
                <option value="512M">512 MB</option>
                <option value="1G">1 GB</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Kernel</span>
              <select
                value={kernel}
                onChange={(e) => setKernel(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-voltainer-500"
              >
                <option value="lts">LTS</option>
                <option value="mainline">Mainline</option>
              </select>
            </label>
          </div>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={autoStart}
              onChange={(e) => setAutoStart(e.target.checked)}
              className="rounded border-gray-300 text-voltainer-600 focus:ring-voltainer-500"
            />
            <span className="text-sm text-gray-700">Start immediately after create</span>
          </label>

          <div className="flex justify-end space-x-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create VM
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
